import { useState, useEffect, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { alertActions } from '../../../store/alert';
import { AddEmployees } from './AddEmployees';
import { AuthContext } from '../../context/auth-context';
import { v4 as uuidv4 } from 'uuid';
import LocationsAPI from '../../../services/api/admin/locations-api';
import EmployeesAPI from '../../../services/api/admin/employees-api';
import Logger from '../../../utils/logger';
import { DataGrid, GridToolbar, GridActionsCellItem } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import { Locations } from '../locations/Locations';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { EmployeeCsvUploadModal } from './EmployeeCsvUploadModal';

const hasLoginPermissions = (emp) =>
  !!(emp.is_admin || emp.is_timecard || emp.is_workflow || emp.is_analytics);

export const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [locationsModalOpen, setLocationsModalOpen] = useState(false);
  const [csvUploadModalOpen, setCsvUploadModalOpen] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState({
    id: '',
    firstname: '',
    lastname: '',
    email: '',
    coins_empl: '',
    home_union: '',
    is_admin: false,
    is_timecard: false,
    is_workflow: false,
    is_analytics: false,
    location_id: '',
    employment_status_id: 'active',
  });
  const [employmentStatuses, setEmploymentStatuses] = useState([]);
  const [editEmployee, setEditEmployee] = useState(false);
  const [originalHadCredentials, setOriginalHadCredentials] = useState(false);
  // Dialog shown when a delete is blocked because the employee has a user
  // (login) account. Options depend on whether they have ever logged in.
  const [userAccountDialog, setUserAccountDialog] = useState({
    open: false,
    employee: null,
    hasLoggedIn: false,
  });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    employee: null,
  });

  const auth = useContext(AuthContext);
  const dispatch = useDispatch();

  useEffect(() => {
    const getEmployees = async () => {
      try {
        const employeesData = await EmployeesAPI.getAll(auth.token);
        Logger.info('Employees retrieved successfully', {
          count: employeesData?.length,
        });
        setEmployees(employeesData);
      } catch (err) {
        alert('There was an error getting the employees');
        Logger.error('Error getting the employees', err);
      }
    };
    if (auth.token) {
      getEmployees();
    }
  }, [auth.token]);

  useEffect(() => {
    const getLocations = async () => {
      try {
        const locationsData = await LocationsAPI.getAll(auth.token);
        Logger.debug('Locations fetched', { count: locationsData?.length });
        setLocations(locationsData || []);
      } catch (err) {
        Logger.error('Error getting the locations', err);
        setLocations([]);
      }
    };
    if (auth.token) {
      getLocations();
    }
  }, [auth.token]);

  useEffect(() => {
    const getEmploymentStatuses = async () => {
      try {
        const statuses = await EmployeesAPI.getEmploymentStatuses(auth.token);
        setEmploymentStatuses(statuses);
      } catch (err) {
        Logger.error('Error getting employment statuses', err);
      }
    };
    if (auth.token) {
      getEmploymentStatuses();
    }
  }, [auth.token]);

  const handleOpen = () => {
    setCurrentEmployee({
      id: '',
      firstname: '',
      lastname: '',
      email: '',
      coins_empl: '',
      home_union: '',
      is_admin: false,
      is_timecard: false,
      is_workflow: false,
      is_analytics: false,
      location_id: '',
      employment_status_id: 'active',
    });
    setEditEmployee(false);
    setOpen(true);
  };

  const handleAddEmployee = async (employee) => {
    const id = uuidv4();
    // Prepare employee data (without credential fields for the employee endpoint)
    const employeeData = {
      id: id,
      firstname: employee.firstname,
      lastname: employee.lastname,
      coins_empl: employee.coins_empl,
      home_union: employee.home_union,
      location_id: employee.location_id,
      employment_status_id: employee.employment_status_id || 'active',
      email: employee.email,
    };

    Logger.userAction('Adding new employee', { employee: { ...employee, id } });

    // Step 1: Create the employee record.
    // A duplicate name/COINS Id throws a 409 here, which propagates to the
    // modal's submit handler so it can show an inline error and stay open.
    const addedEmployee = await EmployeesAPI.create(employeeData, auth.token);
    Logger.info('Employee added successfully', { employee: addedEmployee });
    const employeeId = addedEmployee?.id || id;

    // Step 2: If a login permission is checked, create login credentials.
    // Credential failures are non-fatal — the employee was still created.
    if (hasLoginPermissions(employee)) {
      if (employee.employment_status_id !== 'active') {
        alert(
          'Cannot create login credentials for non-active employees. Employee was created without login access.'
        );
      } else {
        try {
          await EmployeesAPI.createCredentials(
            employeeId,
            {
              email: employee.email,
              isAdmin: employee.is_admin,
              isTimecard: employee.is_timecard,
              isWorkflow: employee.is_workflow,
              isAnalytics: employee.is_analytics,
            },
            auth.token
          );
          Logger.info('Credentials created successfully', { employeeId });
        } catch (credErr) {
          Logger.error('Error creating credentials', credErr);
          alert(
            `Employee created but failed to create login credentials: ${
              credErr.response?.data?.message || credErr.message
            }`
          );
        }
      }
    }

    // Success: refresh the list and close the modal.
    try {
      const refreshedEmployees = await EmployeesAPI.getAll(auth.token);
      setEmployees(refreshedEmployees);
    } catch (err) {
      Logger.error('Error refreshing employees after add', err);
    }
    setEditEmployee(false);
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const requestDeleteEmployee = (id) => {
    const employee = employees.find((e) => e.id === id) || { id };
    setDeleteConfirmDialog({ open: true, employee });
  };

  const closeDeleteConfirmDialog = () =>
    setDeleteConfirmDialog({ open: false, employee: null });

  const confirmDeleteEmployee = async () => {
    const employee = deleteConfirmDialog.employee;
    if (!employee?.id) return;

    const id = employee.id;
    closeDeleteConfirmDialog();

    try {
      await EmployeesAPI.delete(id, auth.token);
      Logger.info('Employee deleted successfully', { employeeId: id });
      const refreshed = await EmployeesAPI.getAll(auth.token);
      setEmployees(refreshed);
    } catch (err) {
      const apiErr = err.response?.data?.error;
      // Blocked because the employee has a user account → show a clear dialog
      // with the right options instead of a raw error.
      if (apiErr?.reason === 'employee_has_user_account') {
        const employee = employees.find((e) => e.id === id) || { id };
        setUserAccountDialog({
          open: true,
          employee,
          hasLoggedIn: !!apiErr.data?.has_logged_in,
        });
        return;
      }
      if (apiErr?.reason === 'employee_has_tracked_time') {
        dispatch(
          alertActions.showAlert({
            title: 'Cannot delete this employee',
            message:
              err.response?.data?.message ||
              'You can not delete an employee which has already tracked time.',
          })
        );
        return;
      }
      Logger.error('Error deleting the employee in the backend', err);
      dispatch(
        alertActions.showAlert({
          title: 'Error deleting employee',
          message:
            err.response?.data?.message ||
            'There was an error deleting the employee',
        })
      );
    }
  };

  const closeUserAccountDialog = () =>
    setUserAccountDialog({ open: false, employee: null, hasLoggedIn: false });

  // Never-logged-in employee: delete both the employee and user records.
  const handleDeleteWithUser = async () => {
    const employee = userAccountDialog.employee;
    if (!employee) return;
    try {
      await EmployeesAPI.deleteWithUser(employee.id, auth.token);
      Logger.info('Employee + user account deleted', {
        employeeId: employee.id,
      });
      const refreshed = await EmployeesAPI.getAll(auth.token);
      setEmployees(refreshed);
    } catch (err) {
      Logger.error('Error deleting employee with user account', err);
      alert(
        err.response?.data?.message ||
          'There was an error deleting the employee and user account'
      );
    } finally {
      closeUserAccountDialog();
    }
  };

  // Has logged in: deletion is blocked. Open the edit modal so the admin can
  // set an inactive employment status instead (which also disables login).
  const handleMakeInactiveFromDelete = () => {
    const employee = userAccountDialog.employee;
    closeUserAccountDialog();
    if (employee) {
      handleEditEmployee({ row: employee });
    }
  };

  const handleUpdateEmployee = async (updatedEmployee) => {
    Logger.userAction('Updating employee', { employee: updatedEmployee });
    const previousEmployee = employees.find((e) => e.id === updatedEmployee.id);
    const previousEmploymentStatusId = previousEmployee?.employment_status_id;
    const statusChangeDate = updatedEmployee.status_change_date;
    const statusChanged =
      previousEmploymentStatusId &&
      previousEmploymentStatusId !==
        (updatedEmployee.employment_status_id || 'active');

    // Prepare employee data (without credential fields)
    const employeeData = {
      id: updatedEmployee.id,
      firstname: updatedEmployee.firstname,
      lastname: updatedEmployee.lastname,
      coins_empl: updatedEmployee.coins_empl,
      home_union: updatedEmployee.home_union,
      location_id: updatedEmployee.location_id,
      employment_status_id: updatedEmployee.employment_status_id,
      status_change_date: updatedEmployee.status_change_date,
      email: updatedEmployee.email,
    };

    // Step 1: Update the employee record.
    // A duplicate name/COINS Id throws a 409 here, which propagates to the
    // modal's submit handler so it can show an inline error and stay open.
    const modifiedEmployee = await EmployeesAPI.update(
      employeeData,
      {
        previousEmploymentStatusId: previousEmploymentStatusId,
        statusChangeDate: statusChangeDate
          ? statusChangeDate.toISOString().split('T')[0] + ' 00:00:00'
          : null,
        statusChangedBy: statusChanged ? auth.userId : null,
        statusChangeNotes: statusChanged ? 'Updated from Employees tab' : null,
      },
      auth.token
    );
    Logger.info('Employee updated successfully', {
      employee: modifiedEmployee,
    });

    // Step 2: Handle credential changes (non-fatal — the employee record was
    // already saved successfully above).
    try {
      const nowHasCredentials = hasLoginPermissions(updatedEmployee);

      if (originalHadCredentials && !nowHasCredentials) {
        // Case 1: Had login and now don't → Revoke credentials
        try {
          await EmployeesAPI.revokeCredentials(updatedEmployee.id, auth.token);
          Logger.info('Credentials revoked successfully', {
            employeeId: updatedEmployee.id,
          });
        } catch (err) {
          Logger.error('Error revoking credentials', err);
          alert('Employee updated but failed to revoke login access.');
        }
      } else if (originalHadCredentials && nowHasCredentials) {
        // Case 2: Had login and still do → Update credentials
        try {
          await EmployeesAPI.updateCredentials(
            updatedEmployee.id,
            {
              email: updatedEmployee.email,
              isAdmin: updatedEmployee.is_admin,
              isTimecard: updatedEmployee.is_timecard,
              isWorkflow: updatedEmployee.is_workflow,
              isAnalytics: updatedEmployee.is_analytics,
            },
            auth.token
          );
          Logger.info('Credentials updated successfully', {
            employeeId: updatedEmployee.id,
          });
        } catch (err) {
          Logger.error('Error updating credentials', err);
          alert('Employee updated but failed to update login credentials.');
        }
      } else if (!originalHadCredentials && nowHasCredentials) {
        // Case 3: Didn't have login but now do → Create credentials
        if (updatedEmployee.employment_status_id !== 'active') {
          alert(
            'Cannot create login credentials for non-active employees. Employee was updated without login access.'
          );
        } else {
          try {
            await EmployeesAPI.createCredentials(
              updatedEmployee.id,
              {
                email: updatedEmployee.email,
                isAdmin: updatedEmployee.is_admin,
                isTimecard: updatedEmployee.is_timecard,
                isWorkflow: updatedEmployee.is_workflow,
                isAnalytics: updatedEmployee.is_analytics,
              },
              auth.token
            );
            Logger.info('Credentials created successfully', {
              employeeId: updatedEmployee.id,
            });
          } catch (err) {
            Logger.error('Error creating credentials', err);
            alert(
              `Employee updated but failed to create login credentials: ${
                err.response?.data?.message || err.message
              }`
            );
          }
        }
      }
    } catch (credErr) {
      Logger.error('Error handling credential changes', credErr);
    }

    // Success: refresh the list and close the modal.
    try {
      const refreshedEmployees = await EmployeesAPI.getAll(auth.token);
      setEmployees(refreshedEmployees);
    } catch (err) {
      Logger.error('Error refreshing employees after update', err);
    }
    setOpen(false);
  };

  const handleEditEmployee = async (id) => {
    const employee = id.row;

    // Check if the employee has login credentials
    let hasCredentials = false;
    let credentialData = {};

    try {
      const credentialsData = await EmployeesAPI.getCredentials(
        employee.id,
        auth.token
      );
      if (credentialsData.hasCredentials) {
        hasCredentials = true;
        credentialData = credentialsData.credentials || {};
      }
    } catch (err) {
      Logger.debug(
        'Error checking credentials (may be expected if employee has no credentials)',
        err
      );
      // If 404, employee has no credentials - that's ok
    }

    setOriginalHadCredentials(hasCredentials);

    setCurrentEmployee({
      id: employee.id,
      firstname: employee.firstname,
      lastname: employee.lastname,
      email: hasCredentials ? credentialData.email || '' : '',
      coins_empl: employee.coins_empl === null ? '' : employee.coins_empl,
      home_union: employee.home_union === null ? '' : employee.home_union,
      is_admin: hasCredentials ? credentialData.is_admin || false : false,
      is_timecard: hasCredentials ? credentialData.is_timecard || false : false,
      is_workflow: hasCredentials ? credentialData.is_workflow || false : false,
      is_analytics: hasCredentials
        ? credentialData.is_analytics || false
        : false,
      location_id: employee.location_id,
      employment_status_id: employee.employment_status_id || 'active',
    });
    setEditEmployee(true);
    setOpen(true);
    Logger.debug('Editing employee', { employeeId: id.row, hasCredentials });
  };

  const columns = [
    {
      field: 'firstname',
      headerName: 'First Name',
      flex: 1,
      minWidth: 80,
      editable: false,
    },
    {
      field: 'lastname',
      headerName: 'Last Name',
      flex: 1,
      minWidth: 80,
      editable: false,
    },
    {
      field: 'location_name',
      headerName: 'Location',
      flex: 1.5,
      minWidth: 120,
      editable: false,
    },
    {
      field: 'home_union',
      headerName: 'Home Union',
      flex: 0.8,
      minWidth: 80,
      editable: false,
    },
    {
      field: 'temporary_working_union',
      headerName: 'Temp Union',
      flex: 0.8,
      minWidth: 80,
      editable: false,
      renderCell: (params) => {
        if (params.value) {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: '#ff9800',
              }}
            >
              <WarningIcon sx={{ fontSize: 18 }} />
              <span>{params.value}</span>
            </Box>
          );
        }
        return '';
      },
    },
    {
      field: 'employment_status_name',
      headerName: 'Employment Status',
      flex: 1.2,
      minWidth: 100,
      editable: false,
      valueGetter: (params) => {
        const statusName = params.row.employment_status_name || 'Unknown';
        const lastChangeDate = params.row.last_status_change_date;
        const isActive = params.row.employment_status_id === 'active';

        if (!isActive && lastChangeDate) {
          const date = new Date(lastChangeDate);
          const formattedDate = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          });
          return `${statusName} (${formattedDate})`;
        }
        return statusName;
      },
    },
    {
      field: 'coins_empl',
      headerName: 'COINS ID',
      flex: 0.8,
      minWidth: 80,
      editable: false,
    },
    {
      field: 'is_timecard',
      headerName: 'Timecard',
      flex: 0.55,
      minWidth: 72,
      editable: false,
      type: 'boolean',
    },
    {
      field: 'is_workflow',
      headerName: 'Workflow',
      flex: 0.55,
      minWidth: 72,
      editable: false,
      type: 'boolean',
    },
    {
      field: 'is_analytics',
      headerName: 'Analytics',
      flex: 0.55,
      minWidth: 72,
      editable: false,
      type: 'boolean',
    },
    {
      field: 'is_admin',
      headerName: 'Admin',
      flex: 0.6,
      minWidth: 70,
      editable: false,
      type: 'boolean',
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 2,
      minWidth: 150,
      editable: false,
    },
    {
      field: 'actions',
      type: 'actions',
      width: 80,
      headerName: '',
      cellClassName: 'actions',
      getActions: (id) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          name="edit"
          label="Edit"
          onClick={() => {
            handleEditEmployee(id);
          }}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon color="action" />}
          name="delete"
          label="Delete"
          onClick={() => {
            requestDeleteEmployee(id.id);
          }}
          color="inherit"
        />,
      ],
    },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 2,
        paddingLeft: 2,
        paddingRight: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
        <Button variant="contained" onClick={() => handleOpen()}>
          Add Employee
        </Button>
        <Button variant="outlined" onClick={() => setLocationsModalOpen(true)}>
          Edit Locations
        </Button>
        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={() => setCsvUploadModalOpen(true)}
        >
          Upload CSV
        </Button>
      </Box>
      <AddEmployees
        employee={currentEmployee}
        editEmployee={editEmployee}
        open={open}
        handleCancel={handleCancel}
        handleAddEmployee={handleAddEmployee}
        handleUpdateEmployee={handleUpdateEmployee}
        locations={locations || []}
        employmentStatuses={employmentStatuses}
        previousEmploymentStatusId={
          employees.find((e) => e.id === currentEmployee.id)
            ?.employment_status_id
        }
        originalHadCredentials={originalHadCredentials}
        onSearchLastname={(lastname) =>
          EmployeesAPI.searchByLastname(lastname, auth.token)
        }
        onViewMatch={(employeeId) => {
          const emp = employees.find((e) => e.id === employeeId);
          if (emp) {
            // Switch the open modal from Add to Edit for the existing employee
            handleEditEmployee({ row: emp });
          }
        }}
      />
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={closeDeleteConfirmDialog}
      >
        <DialogTitle>Delete employee</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            {deleteConfirmDialog.employee?.firstname || ''}{' '}
            {deleteConfirmDialog.employee?.lastname || 'this employee'}? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirmDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteEmployee}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={userAccountDialog.open} onClose={closeUserAccountDialog}>
        <DialogTitle>
          {userAccountDialog.hasLoggedIn
            ? 'Cannot delete this employee'
            : 'This employee has a user account'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {userAccountDialog.hasLoggedIn
              ? `${userAccountDialog.employee?.firstname || ''} ${
                  userAccountDialog.employee?.lastname || ''
                } has a user account with login history. To preserve their records, you can make them inactive instead.`
              : `${userAccountDialog.employee?.firstname || ''} ${
                  userAccountDialog.employee?.lastname || ''
                } has system access but has never logged in. You can delete both the employee record and their user account together.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUserAccountDialog}>Cancel</Button>
          {userAccountDialog.hasLoggedIn ? (
            <Button variant="contained" onClick={handleMakeInactiveFromDelete}>
              Make Inactive
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteWithUser}
            >
              Delete Employee and User Account
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {locationsModalOpen && (
        <Locations
          onClose={() => {
            setLocationsModalOpen(false);
            // Refresh locations data when modal closes
            const getLocations = async () => {
              try {
                const locationsData = await LocationsAPI.getAll(auth.token);
                setLocations(locationsData);
              } catch (err) {
                Logger.error('Error getting the locations', err);
              }
            };
            getLocations();
          }}
        />
      )}
      <EmployeeCsvUploadModal
        open={csvUploadModalOpen}
        onClose={() => setCsvUploadModalOpen(false)}
        onUploadComplete={() => {
          // Refresh employees list after upload
          const getEmployees = async () => {
            try {
              const employeesData = await EmployeesAPI.getAll(auth.token);
              setEmployees(employeesData);
            } catch (err) {
              alert('There was an error getting the employees');
              Logger.error('Error getting the employees', err);
            }
          };
          getEmployees();
        }}
      />
      <Box
        sx={{
          width: '98%',
          flex: 1,
          minHeight: 0,
        }}
      >
        <DataGrid
          getRowId={(row) => row.id}
          columns={columns}
          rows={employees}
          // rowHeight={20}
          components={{ Toolbar: GridToolbar }}
          componentsProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
              showQuickFilter: true,
            },
          }}
          initialState={{
            filter: {
              filterModel: {
                items: [],
              },
            },
          }}
          disableDensitySelector={true}
          disableColumnSelector={true}
        />
      </Box>
    </Box>
  );
};
